import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Home, Building2, HardHat, Search, Lock, CheckCircle2, ChevronDown } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { BRAND } from '@/data/constants';

import { fetchListings } from '@/lib/marketplace';
import { deriveStats, EMPTY_STATS, MarketplaceStats } from '@/lib/homeStats';
import JobCard, { Listing } from '@/components/JobCard';
import Seo from '@/components/Seo';

import HeroVideo from '@/components/home/HeroVideo';
import CategoryStrip from '@/components/home/CategoryStrip';
import CtaBand from '@/components/home/CtaBand';
import TrustSection from '@/components/home/TrustSection';
import Reveal from '@/components/home/Reveal';

/** Anchor target for the hero's "How It Works" button. */
const HOW_IT_WORKS_ID = 'how-it-works';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * HOME PAGE — ORDERED AROUND ONE QUESTION: "WHAT WORK CAN I GET TODAY?"
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Structure (from the home page audit):
 *   1. Hero            — headline, CTAs, marketplace stats, ONE disclaimer
 *   2. Browse by trade — the single trade section, high on the page
 *   3. Open opportunities — the real inventory; the strongest asset we have
 *   4. How it works    — now that they have seen work, explain the mechanics
 *   5. Role paths      — pick your lane
 *   6. Grow your business — conversion messaging, AFTER the value is proven
 *   7. Trust & transparency
 *   8. FAQ
 *   9. Final CTA
 *
 * THREE THINGS WERE DELIBERATELY DELETED, NOT MOVED:
 *
 * - The "Marketplace at a glance" strip. It sat directly under the hero stats
 *   and re-cut the same rows a second way (58 homeowner + 2 GC = the 60 active
 *   projects already shown above; $357K restated verbatim). That forces the
 *   visitor to reconcile two stat blocks instead of looking at work. The
 *   underlying figures are still computed in src/lib/homeStats.ts and are still
 *   used by the hero, so nothing about the data layer changed.
 *
 * - The "Every trade on the jobsite" photo grid. Trades already appear in
 *   "Browse by trade" near the top; showing all 16 twice put the same
 *   navigation decision in front of the user in two places.
 *
 * - The mid-page "Operated by" company block. That information now lives in
 *   the footer next to the rest of the company/legal details, where it also
 *   appears on every other page rather than only this one.
 */
const AppLayout: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Listing[]>([]);
  const [stats, setStats] = useState<MarketplaceStats>(EMPTY_STATS);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    // One request powers both the feed preview and every headline stat, so the
    // numbers on this page can never disagree with the projects shown below.
    let alive = true;
    fetchListings({ limit: 60 }).then((rows) => {
      if (!alive) return;
      setJobs(rows);
      setStats(deriveStats(rows));
    });
    return () => {
      alive = false;
    };
  }, []);

  // Nine cards instead of six: the feed is now the centrepiece of the page, so
  // it should look like a marketplace with depth rather than a teaser.
  const featured = jobs.slice(0, 9);
  const faqs = [1, 2, 3, 4].map((i) => ({ q: t(`home.faq${i}Q`), a: t(`home.faq${i}A`) }));

  const roles = [
    {
      icon: Home,
      title: t('home.homeownerTitle'),
      desc: t('home.homeownerDesc'),
      to: `${path('/register')}?role=homeowner`,
      accent: 'bg-emerald-50 text-emerald-700',
    },
    {
      icon: Building2,
      title: t('home.gcTitle'),
      desc: t('home.gcDesc'),
      to: `${path('/register')}?role=gc`,
      accent: 'bg-blue-50 text-brand-blue',
    },
    {
      icon: HardHat,
      title: t('home.subTitle'),
      desc: t('home.subDesc'),
      to: `${path('/register')}?role=subcontractor`,
      accent: 'bg-orange-50 text-brand-orange',
      featured: true,
    },
  ];

  const steps = [
    { icon: Search, title: t('home.how1Title'), desc: t('home.how1Desc') },
    { icon: HardHat, title: t('home.how2Title'), desc: t('home.how2Desc') },
    { icon: Lock, title: t('home.how3Title'), desc: t('home.how3Desc') },
  ];

  return (
    <div className="bg-brand-canvas">
      <Seo
        title={lang === 'es' ? 'Encuentre trabajo de construcción cerca de usted' : 'Find construction work near you'}
        description={lang === 'es' ? BRAND.tagline_es : BRAND.tagline_en}
        schema={{
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />

      {/* 1. HERO — headline, CTAs and the only stats block on the page */}
      <HeroVideo stats={stats} howItWorksId={HOW_IT_WORKS_ID} />

      {/* 2. BROWSE BY TRADE — the single trade section, kept high */}
      <CategoryStrip />

      {/* 3. OPEN OPPORTUNITIES — real inventory, the page's centrepiece */}
      <section className="bg-brand-canvas py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('home.latestJobs')}</h2>
              <p className="mt-2 text-slate-500">{t('home.latestJobsSub')}</p>
            </div>
            <Link
              to={path('/jobs')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-card"
            >
              {t('common.viewAll')} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((j, i) => (
              <Reveal key={j.id} delay={i * 70}>
                <JobCard job={j} onUnlock={() => navigate(path(`/jobs/${j.slug}`))} />
              </Reveal>
            ))}
          </div>

          {/* A second route into the full feed after nine cards, so a visitor who
              scrolled the whole grid does not have to scroll back up. */}
          {jobs.length > featured.length && (
            <div className="mt-10 text-center">
              <Link
                to={path('/jobs')}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-navy px-6 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-800"
              >
                {t('home.ctaBrowseProjects')} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      <section id={HOW_IT_WORKS_ID} className="scroll-mt-24 bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('home.howTitle')}</h2>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 90}>
                <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover">
                  <span className="absolute -top-4 left-6 flex h-8 w-8 items-center justify-center rounded-full bg-brand-navy text-sm font-extrabold text-white">
                    {i + 1}
                  </span>
                  <s.icon className="mt-3 h-7 w-7 text-brand-orange" />
                  <h3 className="mt-4 text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. ROLE PATHS */}
      <section className="bg-brand-canvas py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t('home.pathTitle')}</h2>
            <p className="mt-3 text-base text-slate-500">{t('home.pathSub')}</p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {roles.map((r, i) => (
              <Reveal key={r.title} delay={i * 80}>
                <Link
                  to={r.to}
                  className={`group flex h-full flex-col rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover ${
                    r.featured
                      ? 'border-orange-300 bg-orange-50/50 shadow-card ring-1 ring-orange-200'
                      : 'border-slate-200 bg-white shadow-sm'
                  }`}
                >
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${r.accent}`}>
                    <r.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 text-xl font-bold text-slate-900">{r.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{r.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange">
                    {t('home.getStarted')}{' '}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. READY TO GROW YOUR BUSINESS — conversion band, moved down the page
             so it arrives after the visitor has seen real work and pricing
             mechanics rather than before. */}
      <CtaBand />

      {/* 7. TRUST & TRANSPARENCY — verifiable facts only */}
      <TrustSection />

      {/* 8. FAQ */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('home.faqTitle')}</h2>
          <div className="mt-8 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
            {faqs.map((f, i) => (
              <div key={f.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50"
                  aria-expanded={openFaq === i}
                >
                  <span className="text-base font-bold text-slate-900">{f.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <p className="animate-fade-in px-5 pb-5 text-sm leading-relaxed text-slate-600">{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section className="bg-brand-canvas pb-12 pt-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-brand-orange to-orange-600 px-6 py-12 text-center shadow-glow sm:px-12">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {t('home.ctaBottomTitle')}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-orange-50">{t('home.ctaBottomSub')}</p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to={profile ? path('/dashboard') : `${path('/register')}?role=subcontractor`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-navy px-6 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-800"
              >
                <CheckCircle2 className="h-5 w-5" /> {t('home.getStarted')}
              </Link>
              <Link
                to={path('/jobs')}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-4 text-base font-bold text-orange-600 transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-50"
              >
                {t('home.ctaFindWork')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AppLayout;
